// components/LineChart.tsx
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import _ from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { Line } from "react-chartjs-2";

import {
  CountTotalResult,
  DATA_SET_KEYS,
  computeNormalizedSummarizedDataSet,
  countHeroTotal,
  countOpponentTotal,
  countSubOpponentsTotal,
  mapSummarizedDataSet,
  sumOpponentTotal,
  summarizeStatistic,
} from "@/lib/statistics-analytic-utils";
import { NumberArrEntry } from "@/module/service-configs-module/statistics-visualization-module/first-svetofor/charts/bar-chart";
import { colorPool } from "@/module/service-configs-module/statistics-visualization-module/first-svetofor/util";
import { HeroResultsDto, PeriodStatisticsDto } from "@/shared/api";

ChartJS.register(
  LineElement,
  PointElement,
  LineController,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend
);

const options = {
  responsive: true,
  plugins: {
    title: {
      display: true,
      text: "Net Profit by Period",
    },
    legend: {
      display: true,
    },
  },
  scales: {
    y: {
      title: {
        display: true,
        text: "Net Profit by Period",
      },
    },
    x: {
      title: {
        display: true,
        text: "Date",
      },
    },
  },
};
export type MetricKey = "Raked" | "Unraked";

export default function SummaryLine({ data }: { data: PeriodStatisticsDto[] }) {
  const [selectedMetric, setSelectedMetric] = useState<MetricKey>("Raked");

  const summarized = useMemo(() => summarizeStatistic(data), [data]);

  const summarizedFinish = useMemo(() => {
    return summarized
      .map((it) => {
        const heroWinnrateUnraked = it.hero_hands_count
          ? (it.hero_gross_profit_bb / it.hero_hands_count) * 100
          : 0;

        const heroWinnrateRaked = it.hero_hands_count
          ? ((it.hero_gross_profit_bb - it.hero_rake_bb) / it.hero_hands_count) * 100
          : 0;

        const winnerWinnrateUnraked = it.winnerHands
          ? (it.winnerGrossProfitBB / it.winnerHands) * 100
          : 0;

        const winnerWinnrateRaked = it.winnerHands
          ? ((it.winnerGrossProfitBB - it.winnerRakeBB) / it.winnerHands) * 100
          : 0;

        const loserWinnrateUnraked = it.loserHands
          ? (it.loserGrossProfitBB / it.loserHands) * 100
          : 0;

        const loserWinnrateRaked = it.loserHands
          ? ((it.loserGrossProfitBB - it.loserRakeBB) / it.loserHands) * 100
          : 0;

        const subOpponentsWithWinrates = it.subOpponents.map((op) => {
          const winnerUnraked = op.winnerHands
            ? (op.winnerGrossProfitBB / op.winnerHands) * 100
            : 0;

          const winnerRaked = op.winnerHands
            ? ((op.winnerGrossProfitBB - op.winnerRakeBB) / op.winnerHands) * 100
            : 0;

          const loserUnraked = op.loserHands
            ? (op.loserGrossProfitBB / op.loserHands) * 100
            : 0;

          const loserRaked = op.loserHands
            ? ((op.loserGrossProfitBB - op.loserRakeBB) / op.loserHands) * 100
            : 0;

          return {
            winnerWinnrateUnraked: winnerUnraked,
            winnerWinnrateRaked: winnerRaked,
            loserWinnrateUnraked: loserUnraked,
            loserWinnrateRaked: loserRaked,
          };
        });

        const subOpponentFieldsUnraked = subOpponentsWithWinrates.reduce((acc, op, index) => {
          acc[`subopponent_${index}_winner_${selectedMetric}`] = op.winnerWinnrateUnraked;
          acc[`subopponent_${index}_loser_${selectedMetric}`] = op.loserWinnrateUnraked;
          return acc;
        }, {} as Record<string, number>);

        const subOpponentFieldsRaked = subOpponentsWithWinrates.reduce((acc, op, index) => {
          acc[`subopponent_${index}_winner_${selectedMetric}`] = op.winnerWinnrateRaked;
          acc[`subopponent_${index}_loser_${selectedMetric}`] = op.loserWinnrateRaked;
          return acc;
        }, {} as Record<string, number>);

        return {
          period: it.period,
          heroWinnrateRaked,
          winnerWinnrateRaked,
          loserWinnrateRaked,
          ...subOpponentFieldsRaked,
          heroWinnrateUnraked,
          winnerWinnrateUnraked,
          loserWinnrateUnraked,
          ...subOpponentFieldsUnraked,
        };
      })
      .reduce((acc, item) => {
        for (const key in item) {
          // @ts-expect-error valid
          if (typeof item[key] === "number") {
            if (!acc[key]) {
              acc[key] = []; // создаём массив если ещё не создан
            }
            // @ts-expect-error valid
            acc[key].push(item[key]); // добавляем текущее значение
          }
        }
        return acc;
      }, {} as Record<string, number[]>);
  }, [selectedMetric, summarized]);

  return (
    <>
      <select
        style={{ width: "100px" }}
        onChange={(e) => setSelectedMetric(e.target.value as MetricKey)}
      >
        <option value="Raked">Raked</option>
        <option value="Unraked">Unraked</option>
      </select>
      <Line
        key="key12"
        data={{
          labels: data.map((it) => it.period),
          datasets: Object.entries(summarizedFinish)
            .filter(([key]) => key.endsWith(selectedMetric))
            .map(([key, value]: NumberArrEntry, index: number) => ({
              label: key,
              data: value,
              borderWidth: 1,
              backgroundColor: colorPool[index % colorPool.length],
            })),
        }}
        options={options}
      />
    </>
  );
}
